import { useState, useEffect } from "react";
import { useTranslation, Trans } from "react-i18next";
import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Badge } from "./ui/badge";
import { Switch } from "./ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { ConfirmDeleteDialog } from "./ui/confirm-dialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Plus, Pencil, Trash2, Info, Sparkles, FileUp } from "lucide-react";
import { accountApi } from "../lib/api";
import { AiChat } from "./AiChat";
import { ContextsImportModal } from "./ContextsImportModal";

type Context = {
  id: string;
  simulatorId: string;
  nameFr: string;
  nameEn: string;
  descriptionFr?: string | null;
  descriptionEn?: string | null;
  // Champs Markdown internes (N8N) — présents seulement pour ADM/COO (stripAdminFields côté API).
  playerBriefMdFr?: string | null;
  playerBriefMdEn?: string | null;
  agentInstructionsMdFr?: string | null;
  agentInstructionsMdEn?: string | null;
  isSystem: boolean;
  isActive: boolean;
  orderIndex: number;
};

type Simulator = {
  id: string;
  name: string;
  isActive?: boolean;
  simulatorType?: string;
  contextsTemplate?: unknown;
};

const BLUE = "#384872";
const GREEN_TEXT = "#1E7A3C";
const GREEN_BG = "#7CBF94";

export function ContextsTab({ accountId, userProfileType }: { accountId?: string; userProfileType?: string }) {
  const { t, i18n } = useTranslation();
  const isFr = i18n.language === "fr";
  // Création/édition de contextes : alignée sur le backend (ALLOWED_PROFILES de
  // src/api/contexts.ts + ai-extract-contexts.ts) → ORG/MNG (clients) ET ADM/COO.
  // Cohérent avec les messages infoLine3 / contextsShapeScenarios qui annoncent
  // aux clients qu'ils peuvent configurer leurs contextes dès maintenant.
  const canManageContexts = ["ORG", "ADM", "COO", "MNG"].includes(userProfileType || "");
  const isAdmin = ["ADM", "COO"].includes(userProfileType || "");

  const [contextsList, setContextsList] = useState<Context[]>([]);
  const [simulators, setSimulators] = useState<Simulator[]>([]);
  const [selectedSimulatorId, setSelectedSimulatorId] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<Context | null>(null);
  const [creating, setCreating] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<Context | null>(null);
  const [showAiChat, setShowAiChat] = useState(false);
  const [aiChatSimulatorId, setAiChatSimulatorId] = useState<string>("");
  const [companyDescription, setCompanyDescription] = useState("");
  const [showImport, setShowImport] = useState(false);

  useEffect(() => {
    let active = true;
    Promise.all([
      accountApi.getContexts({}, accountId),
      fetch("/api/agents", { credentials: "same-origin" }).then((r) => r.json()).catch(() => ({ agents: [] })),
      accountApi.getCompany(accountId).catch(() => null),
    ])
      .then(([ctxs, agentsJson, company]: [any[], any, any]) => {
        if (!active) return;
        setContextsList(ctxs || []);
        setCompanyDescription(company?.description || "");
        const agents: Simulator[] = Array.isArray(agentsJson) ? agentsJson : agentsJson.agents || agentsJson.simulators || [];
        // On affiche uniquement les simulateurs training actifs ayant un catalogue de contextes
        // (contexts_template peuplé). Les autres (ex: Satisfactor avant migration) n'apparaissent pas.
        const filtered = agents.filter((s) => {
          if (s.simulatorType !== "training") return false;
          if (s.isActive === false) return false;
          const tpl = s.contextsTemplate as any[] | undefined;
          return Array.isArray(tpl) && tpl.length > 0;
        });
        setSimulators(filtered);
      })
      .catch(() => {})
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [accountId]);

  const save = async (data: Record<string, any>) => {
    try {
      if (editing?.id) {
        // Envoi « dirty » : uniquement les champs modifiés vs le contexte d'origine,
        // pour ne jamais nuller un champ non édité (ex. briefs Markdown internes,
        // critiques pour la cohérence des scénarios N8N). Cf. incident AUVERGNE.
        const dirty: Record<string, any> = {};
        for (const k of Object.keys(data)) {
          if (data[k] !== (editing as Record<string, any>)[k]) dirty[k] = data[k];
        }
        const updated = await accountApi.updateContext(editing.id, dirty, accountId);
        setContextsList((prev) => prev.map((c) => (c.id === editing.id ? updated : c)));
      } else {
        if (!data.simulatorId) {
          alert("Veuillez sélectionner un simulateur.");
          return;
        }
        const created = await accountApi.createContext(data, accountId);
        setContextsList((prev) => [...prev, created]);
      }
      setEditing(null);
      setCreating(false);
    } catch (err: any) {
      alert(err.message || t("contexts.errorSaving"));
    }
  };

  // Admin uniquement : relance la génération IA du brief + instructions, puis recharge le contexte.
  const regenerate = async (id: string) => {
    try {
      await accountApi.adminRegenerateContextBrief(id);
      alert(t("contexts.regenerateStarted"));
      // Recharge après un court délai (génération + traduction en arrière-plan).
      setTimeout(async () => {
        try {
          const fresh = await accountApi.getContexts({}, accountId);
          setContextsList(fresh || []);
        } catch { /* ignore */ }
      }, 6000);
    } catch (err: any) {
      alert(err.message || t("contexts.errorSaving"));
    }
  };

  const remove = async (c: Context) => {
    try {
      await accountApi.deleteContext(c.id, accountId);
      setContextsList((prev) => prev.filter((x) => x.id !== c.id));
      setConfirmDelete(null);
    } catch (err: any) {
      alert(err.message || t("contexts.errorDeleting"));
    }
  };

  const toggleActive = async (c: Context) => {
    try {
      const updated = await accountApi.toggleContext(c.id, !c.isActive, accountId);
      setContextsList((prev) => prev.map((x) => (x.id === c.id ? updated : x)));
    } catch (err: any) {
      alert(err.message || t("contexts.errorToggling"));
    }
  };

  if (loading) return <p className="text-sm text-muted-foreground text-center py-8">{t("common.loading")}</p>;

  const simulatorsById = new Map(simulators.map((s) => [s.id, s]));

  // AiChat ouvert ?
  const aiChatSim = simulators.find((s) => s.id === aiChatSimulatorId);
  if (showAiChat && aiChatSim) {
    const existingList = contextsList
      .filter((c) => c.simulatorId === aiChatSim.id)
      .map((c) => `- ${c.nameFr} / ${c.nameEn}`)
      .join("\n");
    return (
      <div className="space-y-3">
        <Button variant="outline" size="sm" onClick={() => { setShowAiChat(false); setAiChatSimulatorId(""); }}>
          {t("contexts.backToGrid")}
        </Button>
        <div style={{ height: "70vh" }}>
          <AiChat
            agentCode="context_creator"
            robotColor="#988EF2"
            bgColor="#f8f5ff"
            context={{
              companyDescription,
              simulatorName: aiChatSim.name,
              simulatorDescription: (aiChatSim as any).description || "",
              existingContexts: existingList || "(aucun contexte existant)",
              language: i18n.language,
            }}
            onItemCreated={async (_type, data) => {
              try {
                const created = await accountApi.createContext({
                  simulatorId: aiChatSim.id,
                  // Ne PAS recopier une langue dans l'autre : si l'IA n'a rempli
                  // qu'une langue, on laisse l'autre vide → la traduction auto
                  // (translateContextEnInBackground) la complète. Recopier ferait
                  // croire au traducteur que tout est rempli → langue non traduite.
                  nameFr: data.nameFr || "",
                  nameEn: data.nameEn || "",
                  descriptionFr: data.descriptionFr,
                  descriptionEn: data.descriptionEn,
                }, accountId);
                setContextsList((prev) => [...prev, created]);
              } catch (err: any) {
                alert(err.message || t("contexts.errorCreating"));
              }
            }}
            onComplete={() => { setShowAiChat(false); setAiChatSimulatorId(""); }}
            title={t("contexts.aiTitle", { simulator: aiChatSim.name })}
            subtitle={t("contexts.aiSubtitle")}
          />
        </div>
      </div>
    );
  }

  const visibleContexts = (selectedSimulatorId
    ? contextsList.filter((c) => c.simulatorId === selectedSimulatorId)
    : contextsList
  ).slice().sort((a, b) => {
    const aName = simulatorsById.get(a.simulatorId)?.name || "";
    const bName = simulatorsById.get(b.simulatorId)?.name || "";
    const cmpSim = aName.localeCompare(bName);
    if (cmpSim !== 0) return cmpSim;
    return a.orderIndex - b.orderIndex;
  });

  return (
    <div className="space-y-4">
      {/* Bandeau pédagogique */}
      <div className="rounded-md bg-blue-50 border border-blue-200 p-3 text-xs text-[#384872]">
        <div className="flex items-start gap-2">
          <Info className="h-4 w-4 mt-0.5 flex-shrink-0" />
          <div>
            <p className="font-medium mb-1">{t("contexts.infoTitle")}</p>
            <p><Trans i18nKey="contexts.infoLine1" components={{ 1: <strong /> }} /></p>
            <p><Trans i18nKey="contexts.infoLine2" components={{ 1: <strong />, 2: <strong /> }} /></p>
            <p className="mt-1"><Trans i18nKey="contexts.infoLine3" components={{ 1: <strong /> }} /></p>
          </div>
        </div>
      </div>

      {/* Bandeau "feature à venir" tant que N8N pas adapté */}
      <div className="rounded-md bg-amber-50 border border-amber-200 p-3 text-xs text-amber-900">
        <div className="flex items-start gap-2">
          <Sparkles className="h-4 w-4 mt-0.5 flex-shrink-0" />
          <p><Trans i18nKey="contexts.contextsShapeScenarios" components={{ 1: <strong /> }} /></p>
        </div>
      </div>

      {/* Barre d'actions */}
      <div className="flex flex-col sm:flex-row gap-3 sm:items-end sm:justify-between">
        <div className="flex-1 max-w-xs">
          <Label className="text-xs text-muted-foreground">{t("contexts.filterBySimulator")}</Label>
          <Select value={selectedSimulatorId || "__all__"} onValueChange={(v) => setSelectedSimulatorId(v === "__all__" ? "" : v)}>
            <SelectTrigger className="mt-1">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">{t("contexts.allSimulators")}</SelectItem>
              {simulators.map((s) => (
                <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {!creating && !editing && canManageContexts && (
          <div className="flex flex-wrap gap-2">
            <Button
              size="sm"
              variant="outline"
              className="border-[#988EF2] text-[#5A4BBE] hover:bg-[#988EF2]/10"
              onClick={() => setShowImport(true)}
            >
              <FileUp className="h-4 w-4 mr-2" /> {t("contexts.importDocument")}
            </Button>
            <AiCreateContextButton
              simulators={simulators}
              selectedSimulatorId={selectedSimulatorId}
              onStart={(simId) => { setAiChatSimulatorId(simId); setShowAiChat(true); }}
            />
            <Button
              size="sm"
              data-tour="context-new"
              className="bg-[#384872] hover:bg-[#2d3a5c]"
              onClick={() => { setCreating(true); setEditing(null); }}
            >
              <Plus className="h-4 w-4 mr-2" /> {t("contexts.newContext")}
            </Button>
          </div>
        )}
      </div>

      {/* Form création */}
      {creating && canManageContexts && (
        <div data-tour="context-form">
        <ContextForm
          simulatorName=""
          simulators={simulators}
          defaultSimulatorId={selectedSimulatorId || simulators[0]?.id}
          context={null}
          isFr={isFr}
          isAdmin={isAdmin}
          onSave={save}
          onCancel={() => { setCreating(false); setEditing(null); }}
        />
        </div>
      )}

      {/* Form édition */}
      {editing && !creating && (
        <ContextForm
          simulatorName={simulatorsById.get(editing.simulatorId)?.name || ""}
          simulators={simulators}
          defaultSimulatorId={editing.simulatorId}
          context={editing}
          isFr={isFr}
          isAdmin={isAdmin}
          onSave={save}
          onCancel={() => setEditing(null)}
          onRegenerate={regenerate}
        />
      )}

      {/* Liste compacte */}
      {visibleContexts.length === 0 && !creating ? (
        <div className="text-center py-12">
          <Info className="h-12 w-12 mx-auto text-muted-foreground/30 mb-3" />
          <p className="text-muted-foreground">{t("contexts.noContexts")}</p>
        </div>
      ) : (
        <div className="space-y-1.5">
          {visibleContexts.map((c) => {
            const simName = simulatorsById.get(c.simulatorId)?.name || "?";
            const desc = isFr ? (c.descriptionFr || c.descriptionEn) : (c.descriptionEn || c.descriptionFr);
            return (
              <Card key={c.id} className={!c.isActive ? "opacity-60" : ""}>
                <CardContent className="!py-2 !px-3">
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex-1 min-w-0 flex items-center gap-3 flex-wrap">
                      {/* Nom */}
                      <span className="font-medium text-sm whitespace-nowrap" style={{ color: BLUE }}>
                        {isFr ? (c.nameFr || c.nameEn) : (c.nameEn || c.nameFr)}
                      </span>
                      {/* Description (complète, wrap multi-lignes) */}
                      {desc && (
                        <span className="text-xs text-muted-foreground basis-full whitespace-normal break-words">
                          — {desc}
                        </span>
                      )}
                      {/* Simulateur */}
                      <Badge variant="outline" className="text-xs shrink-0 border-slate-300 text-slate-600">
                        {simName}
                      </Badge>
                      {/* System/Interne */}
                      {c.isSystem ? (
                        <Badge className="bg-slate-200 text-slate-700 hover:bg-slate-200 text-xs shrink-0">{t("contexts.badgeSystem")}</Badge>
                      ) : (
                        <Badge className="text-xs shrink-0" style={{ background: GREEN_BG, color: GREEN_TEXT }}>{t("contexts.badgeInterne")}</Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-1.5 flex-shrink-0">
                      <Switch
                        checked={c.isActive}
                        onCheckedChange={() => toggleActive(c)}
                        aria-label={c.isActive ? t("contexts.ariaDeactivate") : t("contexts.ariaActivate")}
                      />
                      {!c.isSystem && canManageContexts && (
                        <>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-7 w-7 p-0"
                            aria-label={t("contexts.ariaEdit")}
                            onClick={() => { setEditing(c); setCreating(false); }}
                          >
                            <Pencil className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-7 w-7 p-0 text-red-500"
                            aria-label={t("contexts.ariaDelete")}
                            onClick={() => setConfirmDelete(c)}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {confirmDelete && (
        <ConfirmDeleteDialog
          open={!!confirmDelete}
          onCancel={() => setConfirmDelete(null)}
          itemName={(isFr ? confirmDelete.nameFr : confirmDelete.nameEn) || t("contexts.fallbackName")}
          onConfirm={() => remove(confirmDelete)}
        />
      )}

      <ContextsImportModal
        open={showImport}
        onClose={() => setShowImport(false)}
        accountId={accountId}
        simulators={simulators}
        onCreated={async (count) => {
          if (count > 0) {
            try {
              const refreshed = await accountApi.getContexts({}, accountId);
              setContextsList(refreshed || []);
            } catch {}
          }
        }}
      />
    </div>
  );
}

function AiCreateContextButton({
  simulators,
  selectedSimulatorId,
  onStart,
}: {
  simulators: Simulator[];
  selectedSimulatorId: string;
  onStart: (simulatorId: string) => void;
}) {
  const { t } = useTranslation();
  const [picking, setPicking] = useState(false);
  const [pickedId, setPickedId] = useState("");

  const handleClick = () => {
    if (selectedSimulatorId) {
      onStart(selectedSimulatorId);
    } else {
      setPicking(true);
    }
  };

  return (
    <>
      <Button
        size="sm"
        variant="outline"
        className="border-[#988EF2] text-[#5A4BBE] hover:bg-[#988EF2]/10"
        onClick={handleClick}
      >
        {t("contexts.aiCreate")}
      </Button>
      <Dialog open={picking} onOpenChange={(o) => !o && setPicking(false)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{t("contexts.pickSimulatorTitle")}</DialogTitle>
            <DialogDescription>
              {t("contexts.pickSimulatorDescription")}
            </DialogDescription>
          </DialogHeader>
          <Select value={pickedId} onValueChange={setPickedId}>
            <SelectTrigger>
              <SelectValue placeholder={t("contexts.pickSimulatorPlaceholder")} />
            </SelectTrigger>
            <SelectContent>
              {simulators.map((s) => (
                <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="flex justify-end gap-2">
            <Button variant="outline" size="sm" onClick={() => setPicking(false)}>{t("common.cancel")}</Button>
            <Button
              size="sm"
              className="bg-[#384872] hover:bg-[#2d3a5c]"
              disabled={!pickedId}
              onClick={() => { onStart(pickedId); setPicking(false); setPickedId(""); }}
            >
              {t("contexts.startAssistant")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

function ContextForm({
  simulatorName,
  simulators,
  defaultSimulatorId,
  context,
  isFr,
  isAdmin,
  onSave,
  onCancel,
  onRegenerate,
}: {
  simulatorName: string;
  simulators: Simulator[];
  defaultSimulatorId?: string;
  context: Context | null;
  isFr: boolean;
  isAdmin: boolean;
  onSave: (data: Record<string, any>) => void;
  onCancel: () => void;
  onRegenerate?: (id: string) => Promise<void>;
}) {
  const { t } = useTranslation();
  const [form, setForm] = useState<Record<string, any>>(() => ({
    simulatorId: context?.simulatorId || defaultSimulatorId || "",
    nameFr: context?.nameFr || "",
    nameEn: context?.nameEn || "",
    descriptionFr: context?.descriptionFr || "",
    descriptionEn: context?.descriptionEn || "",
    playerBriefMdFr: context?.playerBriefMdFr || "",
    playerBriefMdEn: context?.playerBriefMdEn || "",
    agentInstructionsMdFr: context?.agentInstructionsMdFr || "",
    agentInstructionsMdEn: context?.agentInstructionsMdEn || "",
  }));

  const isEdit = !!context?.id;
  const lang = isFr ? "fr" : "en"; // langue courante (client mono-langue)
  const [regenerating, setRegenerating] = useState(false);
  const set = (k: string, v: string) => setForm((f) => ({ ...f, [k]: v }));

  // Champ nom/description dans la langue courante (client) ou par langue (admin).
  const clientName = lang === "fr" ? form.nameFr : form.nameEn;
  const clientDesc = lang === "fr" ? form.descriptionFr : form.descriptionEn;

  const canSave = isAdmin
    ? (!!form.nameFr?.trim() || !!form.nameEn?.trim()) && (isEdit || !!form.simulatorId)
    : !!clientName?.trim() && (isEdit || !!form.simulatorId);

  const buildPayload = (): Record<string, any> => {
    if (isAdmin) {
      // Admin : bilingue + champs Markdown internes.
      return {
        simulatorId: form.simulatorId,
        nameFr: form.nameFr, nameEn: form.nameEn,
        descriptionFr: form.descriptionFr, descriptionEn: form.descriptionEn,
        playerBriefMdFr: form.playerBriefMdFr, playerBriefMdEn: form.playerBriefMdEn,
        agentInstructionsMdFr: form.agentInstructionsMdFr, agentInstructionsMdEn: form.agentInstructionsMdEn,
      };
    }
    // Client : mono-langue, l'IA traduit l'autre langue.
    return {
      simulatorId: form.simulatorId,
      _sourceLang: lang,
      name: clientName,
      description: clientDesc,
    };
  };

  const doRegenerate = async () => {
    if (!context?.id || !onRegenerate) return;
    setRegenerating(true);
    try { await onRegenerate(context.id); } finally { setRegenerating(false); }
  };

  const taField = "w-full min-h-[80px] px-3 py-2 rounded-md border border-input bg-background text-sm resize-y";
  const mdField = "w-full min-h-[140px] px-3 py-2 rounded-md border border-input bg-background text-sm font-mono resize-y";

  return (
    <Card className="border-2 border-blue-200">
      <CardContent className="!py-4 space-y-4">
        <p className="text-sm font-medium" style={{ color: BLUE }}>
          {isEdit ? t("contexts.editTitle") : t("contexts.createTitle")}{simulatorName ? <> — <span className="text-muted-foreground">{simulatorName}</span></> : null}
        </p>

        {/* Simulateur (création uniquement) */}
        {!isEdit && (
          <div className="space-y-1">
            <Label>{t("contexts.simulatorLabel")}</Label>
            <Select value={form.simulatorId || ""} onValueChange={(v) => set("simulatorId", v)}>
              <SelectTrigger>
                <SelectValue placeholder={t("contexts.pickSimulatorPlaceholder")} />
              </SelectTrigger>
              <SelectContent>
                {simulators.map((s) => (
                  <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {isAdmin ? (
          /* --- Admin : bilingue --- */
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>{t("contexts.nameLabel", { lang: "FR" })}</Label>
                <Input value={form.nameFr || ""} onChange={(e) => set("nameFr", e.target.value)} placeholder={t("contexts.namePlaceholderFr")} />
              </div>
              <div className="space-y-1">
                <Label>{t("contexts.nameLabel", { lang: "EN" })}</Label>
                <Input value={form.nameEn || ""} onChange={(e) => set("nameEn", e.target.value)} placeholder={t("contexts.namePlaceholderEn")} />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>{t("contexts.descriptionLabel", { lang: "FR" })}</Label>
                <textarea className={taField} value={form.descriptionFr || ""} onChange={(e) => set("descriptionFr", e.target.value)} placeholder={t("contexts.descriptionPlaceholderFr")} />
              </div>
              <div className="space-y-1">
                <Label>{t("contexts.descriptionLabel", { lang: "EN" })}</Label>
                <textarea className={taField} value={form.descriptionEn || ""} onChange={(e) => set("descriptionEn", e.target.value)} placeholder={t("contexts.descriptionPlaceholderEn")} />
              </div>
            </div>

            {/* Champs internes (invisibles du client) : brief joueur + instructions agent */}
            <div className="pt-2 border-t space-y-3">
              <div className="flex items-center justify-between">
                <p className="text-xs font-semibold uppercase tracking-wide" style={{ color: BLUE }}>{t("contexts.adminMarkdownSection")}</p>
                {isEdit && onRegenerate && (
                  <Button variant="outline" size="sm" type="button" onClick={doRegenerate} disabled={regenerating}>
                    <Sparkles className="h-3.5 w-3.5 mr-1.5" />
                    {regenerating ? t("contexts.regenerating") : t("contexts.regenerateBrief")}
                  </Button>
                )}
              </div>
              <p className="text-xs text-muted-foreground">{t("contexts.adminMarkdownHint")}</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label>{t("contexts.playerBriefLabel", { lang: "FR" })}</Label>
                  <textarea className={mdField} value={form.playerBriefMdFr || ""} onChange={(e) => set("playerBriefMdFr", e.target.value)} />
                </div>
                <div className="space-y-1">
                  <Label>{t("contexts.playerBriefLabel", { lang: "EN" })}</Label>
                  <textarea className={mdField} value={form.playerBriefMdEn || ""} onChange={(e) => set("playerBriefMdEn", e.target.value)} />
                </div>
                <div className="space-y-1">
                  <Label>{t("contexts.agentInstructionsLabel", { lang: "FR" })}</Label>
                  <textarea className={mdField} value={form.agentInstructionsMdFr || ""} onChange={(e) => set("agentInstructionsMdFr", e.target.value)} />
                </div>
                <div className="space-y-1">
                  <Label>{t("contexts.agentInstructionsLabel", { lang: "EN" })}</Label>
                  <textarea className={mdField} value={form.agentInstructionsMdEn || ""} onChange={(e) => set("agentInstructionsMdEn", e.target.value)} />
                </div>
              </div>
            </div>
          </>
        ) : (
          /* --- Client : mono-langue (langue courante) ; l'IA traduit l'autre langue --- */
          <>
            <div className="space-y-1">
              <Label>{t("contexts.nameSimple")}</Label>
              <Input
                value={clientName || ""}
                onChange={(e) => set(lang === "fr" ? "nameFr" : "nameEn", e.target.value)}
                placeholder={lang === "fr" ? t("contexts.namePlaceholderFr") : t("contexts.namePlaceholderEn")}
              />
            </div>
            <div className="space-y-1">
              <Label>{t("contexts.descriptionSimple")}</Label>
              <textarea
                className={taField}
                value={clientDesc || ""}
                onChange={(e) => set(lang === "fr" ? "descriptionFr" : "descriptionEn", e.target.value)}
                placeholder={lang === "fr" ? t("contexts.descriptionPlaceholderFr") : t("contexts.descriptionPlaceholderEn")}
              />
            </div>
          </>
        )}

        <div className="flex justify-end gap-2 pt-2 border-t">
          <Button variant="outline" size="sm" onClick={onCancel} type="button">{t("common.cancel")}</Button>
          <Button
            size="sm"
            disabled={!canSave}
            className="bg-[#384872] hover:bg-[#2d3a5c]"
            onClick={() => onSave(buildPayload())}
            type="button"
          >
            {isEdit ? t("common.save") : t("common.create")}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
